/**
 * inlined Object.is polyfill to avoid requiring consumers ship their own
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
 *
 * @param x - The first value to compare.
 * @param y - The second value to compare.
 *
 * @docsCategory preference/lang
 */
function is(x, y) {
  // SameValue algorithm
  if (x === y) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    // Added the nonzero y check to make Flow happy, but it is redundant
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    // Step 6.a: NaN == NaN
    return x !== x && y !== y;
  }
}

/**
 * @description
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 *
 * @example
 * ``` ts
 * isShallowEqual([1, 2, 3], [1, 2, 3]);
 * // => true
 * isShallowEqual({ a: 5, b: {} }, { a: 5, b: {} });
 * // => false
 * ```
 *
 * @param objA - The first object to compare.
 * @param objB - The second object to compare.
 * @returns Returns true when the values of all keys are strictly equal.
 *
 * @docsCategory preference/lang
 * @codeFilter is-shallow-equal
 *
 */
export function isShallowEqual(objA, objB): boolean {
  if (is(objA, objB)) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  for (let i = 0; i < keysA.length; i++) {
    if (
      !Object.prototype.hasOwnProperty.call(objB, keysA[i]) ||
      !is(objA[keysA[i]], objB[keysA[i]])
    ) {
      return false;
    }
  }

  return true;
}
